import { Alert, Box, Group, Stack, Text, Title } from '@mantine/core';
import React from 'react';

import {
  InfluenceTargetPaper,
  InfluenceTargetSelector,
  TrackedButton,
} from '@/components';
import { InfluenceTargetType } from '@/components/InfluenceTargetSelector';
import { useIssuePage } from '@/contexts';

const INFLUENCE_TARGET_SELECTION_ENABLED = false; // Set to false to disable

export interface InfluenceTarget {
  id: string;
  title?: string | null;
  subtitle?: string | null;
  __typename?: string; // Add typename to match GraphQL data structure
}

interface IssuePageInfluenceTargetSectionProps {
  influenceTargets: InfluenceTargetType[];
  setInfluenceTargets: React.Dispatch<
    React.SetStateAction<InfluenceTargetType[]>
  >;
  issueHasInfluenceTargets: boolean;
  styles: Record<string, string>; // For passing in the styles from parent component
  issueInfluenceTargetRels?: InfluenceTarget[]; // Use our defined interface
  leaderName: string;
}

export const IssuePageInfluenceTargetSection: React.FC<
  IssuePageInfluenceTargetSectionProps
> = ({
  influenceTargets,
  setInfluenceTargets,
  issueHasInfluenceTargets,
  issueInfluenceTargetRels,
  styles,
  leaderName,
}) => {
  const { getIsFullEditMode, getIsStaticMode } = useIssuePage();

  // State for the feature-flagged UI
  const [knowsOffices, setKnowsOffices] = React.useState<null | boolean>(null);

  if (!INFLUENCE_TARGET_SELECTION_ENABLED) {
    if (!getIsStaticMode()) {
      return (
        <Stack className={styles.infoSection}>
          <Title order={2}>
            Do you know the offices that this issue impacts?
          </Title>
          <Group mt="md">
            <TrackedButton
              variant={knowsOffices === true ? 'filled' : 'outline'}
              onClick={() => setKnowsOffices(true)}
              style={{ flex: 1 }}
              trackingName="issue_page_influence_target_section_yes_click"
              trackingProperties={{
                knowsOffices: knowsOffices,
              }}
            >
              Yes
            </TrackedButton>
            <TrackedButton
              variant={knowsOffices === false ? 'filled' : 'outline'}
              onClick={() => setKnowsOffices(false)}
              style={{ flex: 1 }}
              trackingName="issue_page_influence_target_section_no_click"
              trackingProperties={{
                knowsOffices: knowsOffices,
              }}
            >
              No
            </TrackedButton>
          </Group>
          <Box mt="md">
            {knowsOffices === true && (
              <Alert variant="dimmed">
                <Stack gap="sm">
                  <Title order={3}>Coming soon...</Title>
                  <Text>
                    Soon, you&apos;ll be able to add offices or measures to your
                    topic and provide voting recommendations to supporters.
                  </Text>
                </Stack>
              </Alert>
            )}
            {knowsOffices === false && (
              <Alert variant="dimmed">
                <Stack gap="sm">
                  <Title order={3}>No problem!</Title>
                  <Text>
                    You can skip this for now. When you&apos;re ready, you can
                    add offices or measures to your topic and provide voting
                    recommendations to supporters.
                  </Text>
                </Stack>
              </Alert>
            )}
          </Box>
        </Stack>
      );
    } else {
      return (
        <Box className={styles.infoSection}>
          <Title order={2}>What offices does this impact?</Title>
          <Stack
            gap="md"
            mt="md"
          >
            <Alert variant="dimmed">
              <Stack gap="sm">
                <Title order={3}>Coming soon...</Title>
                <Text>
                  {leaderName} will share the offices and measures they will be
                  making recommendations for soon.
                </Text>
              </Stack>
            </Alert>
          </Stack>
        </Box>
      );
    }
  }

  return (
    <Box className={styles.infoSection}>
      <Title order={2}>What offices does this impact?</Title>
      <Stack>
        {getIsFullEditMode() ? (
          <InfluenceTargetSelector
            influenceTargets={influenceTargets}
            onAddInfluenceTarget={(target) => {
              setInfluenceTargets([...influenceTargets, target]);
            }}
            onRemoveInfluenceTarget={(index) => {
              setInfluenceTargets(
                influenceTargets.filter((_, i) => i !== index),
              );
            }}
            oldInfluenceTargets={issueInfluenceTargetRels?.map((target) => ({
              id: target.id,
              title: target.title || '',
              subtitle: target.subtitle || '',
            }))}
            onRemoveOldInfluenceTarget={(index) => {
              setInfluenceTargets(
                influenceTargets.filter((_, i) => i !== index),
              );
            }}
          />
        ) : issueHasInfluenceTargets ? (
          // all influence targets in view mode
          issueInfluenceTargetRels?.map((target, index) => (
            <InfluenceTargetPaper
              key={target.id}
              title={target.title || ''}
              subtitle={target.subtitle || ''}
              onRemove={() => {
                setInfluenceTargets(
                  influenceTargets.filter((_, i) => i !== index),
                );
              }}
              index={index}
            />
          ))
        ) : (
          <Text
            size="md"
            className={styles.noInfluenceTargets}
          >
            No offices selected.
          </Text>
        )}
      </Stack>
    </Box>
  );
};
